import { SCENE } from '@/app/constants';
import type { TemplateFormParams } from '@/app/stores/slices/task-form';
import type { ImagePrompt, TaskInputPrompts, TaskParams } from '@/app/types';
import { convertImagePromptsFormToAPI, hasUploadedImagePrompt } from '@/app/utils';
import type { ViduV1TaskExtraParam } from '../types';

export const convertTemplate = (formParams: TemplateFormParams): TaskParams => {
  const textInput = formParams.textPrompt ? [{ type: 'text' as const, content: formParams.textPrompt }] : [];

  // 根据 maxImageCount 决定提交的图片数量
  const limitedImagePrompts = formParams.maxImageCount
    ? formParams.imagePrompts.slice(0, formParams.maxImageCount)
    : formParams.imagePrompts;

  const imageInput = convertImageInput(
    limitedImagePrompts.filter((p) => p !== null),
    formParams.isMergeImage,
  );

  const prompts = [...textInput, ...imageInput];

  // has_audio 不放在 settings 中（has_audio 提交到 prompts 中）
  const { has_audio, ...validSettings } = formParams.settings ?? {};

  // 如果模版设置了有配乐，则需要添加音频 prompt
  if (has_audio) {
    prompts.push({
      type: 'audio',
      content: '',
    });
  }

  return {
    input: {
      prompts,
      enhance: false,
    },
    type: SCENE,
    settings: validSettings,
    ...(formParams.inspirationId && {
      extra_params: {
        recreate_source: (formParams.inspirationFrom as ViduV1TaskExtraParam['recreate_source']) || 'inspiration',
        recreate_source_id: formParams.inspirationId,
      },
    }),
  };
};

/**
 * 将模版类型表单输入的图片 prompts 转换为提交任务接口所需格式
 *
 * @param imagePrompts 图片 prompts
 * @param isMergeImage 是否需要合并图片
 * @returns 提交任务接口所需格式
 */
const convertImageInput = (
  templateImagePrompts: { prompt: ImagePrompt | null; presetDemoId?: number }[],
  isMergeImage?: boolean,
): TaskInputPrompts[] => {
  if (!templateImagePrompts?.length) return [];

  // 如果是模版场景，并且是合并图片的任务
  if (isMergeImage) {
    // 源图和选中图都必须已补传，避免 temp:// 哨兵漏进 content / src_imgs。
    const sceneImages = templateImagePrompts
      .map((p) => p?.prompt)
      .filter((p): p is NonNullable<typeof p> => hasUploadedImagePrompt(p));

    if (!sceneImages.length) return [];

    const result: TaskInputPrompts = {
      type: 'image',
      content: sceneImages[0].mergeImage ? sceneImages[0].mergeImage : sceneImages[0].selectedImage,
      src_imgs: sceneImages.map((p) => p.sourceImage),
    };

    // 当合并图片时，如果存在多个 presetDemoId，我们默认取第一个
    const firstPresetDemoId = templateImagePrompts.find((p) => p?.presetDemoId)?.presetDemoId;
    if (firstPresetDemoId) {
      result.template = {
        preset_demo_id: firstPresetDemoId,
      };
    }
    return [result];
  }

  // 非合并图片的场景
  const validTemplateImagePrompts = templateImagePrompts.filter((p) => hasUploadedImagePrompt(p.prompt));
  const result = convertImagePromptsFormToAPI(validTemplateImagePrompts.map((p) => p.prompt));

  // 为每个 prompt 添加对应的 presetDemoId
  result.forEach((p, idx) => {
    const presetDemoId = validTemplateImagePrompts[idx]?.presetDemoId;
    if (presetDemoId) {
      p.template = {
        preset_demo_id: presetDemoId,
      };
    }
  });

  return result;
};
